/**
 * 외주(gtli) 관리자 파리티 화면 공용 유틸/컴포넌트.
 *
 * 대상 3개 화면(차량 관리·세차 차량 기록·세차요금 기록)은 외주 관리자를 "똑같이" 재현한다.
 * 데이터 소스: GET /owner-console/modernwash/vendor-parity/*  (라이브 read-only 어댑터 봉투).
 *
 * 어댑터 봉투(AdapterEnvelope):
 *   { enabled, scope:{role,storeId,storeName,storeCode,linkedToModernwash}|null,
 *     source:{ reachable, degraded, fetchedAt, error:{code,message}|null },
 *     items?: [], data?: {}, reason?: string|null }
 *
 * ⚠️ 이 화면들은 운영 승인(1회 예외)으로 민감 컬럼(email·승인번호·카드사·번호판·이미지)을 노출한다.
 *    접근은 백엔드에서 master 또는 연동매장(jeonju) 매니저로 강제되고, 플래그(기본 off)로 봉인된다.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import api from '../../api/client';

export const surfaceSx = {
  borderRadius: '12px',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  backgroundColor: '#fff',
};

/** now 기준 KST 날짜(YYYY-MM-DD), offsetDays 일 전. */
export const kstDate = (offsetDays = 0) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(
    new Date(Date.now() - offsetDays * 86400000),
  );

/** ms epoch → 'YYYY.MM.DD HH:mm:ss' (KST). null/무효 → '-'. 벤더 format_time 재현. */
export function formatVendorDateTime(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return '-';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '-';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const g = (t) => p.find((x) => x.type === t)?.value ?? '';
  return `${g('year')}.${g('month')}.${g('day')}. ${g('hour')}:${g('minute')}:${g('second')}`;
}

/** period_start/end 등 ms|문자열 → 'YYYY.MM.DD'. 문자열이면 그대로, 무효면 '-'. */
export function formatVendorDate(v) {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (Number.isFinite(n) && String(v).trim() !== '') {
    const ms = n < 1e12 ? n * 1000 : n;
    const d = new Date(ms);
    if (!Number.isNaN(d.getTime())) {
      const p = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(d);
      const g = (t) => p.find((x) => x.type === t)?.value ?? '';
      return `${g('year')}.${g('month')}.${g('day')}`;
    }
  }
  return String(v);
}

/** period_end 가 지났는지(만료) — 벤더의 노랑 하이라이트 조건 재현. */
export function isExpired(periodEnd) {
  if (periodEnd == null || periodEnd === '') return false;
  const n = Number(periodEnd);
  const ms = Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : Date.parse(periodEnd);
  return Number.isFinite(ms) && ms < Date.now();
}

/**
 * 어댑터 봉투 상태 배너.
 *   - enabled=false        → "연동 준비 중"(플래그 off)
 *   - reason(비연동)       → 안내(장애 아님)
 *   - source.error         → 원천 오류(값 없음을 0 으로 단정하지 않기 위해 배너로 표시)
 *   - source.degraded      → 지연 표시
 * 정상이면 null.
 */
export function SourceBanner({ res }) {
  if (!res) return null;
  const { enabled, source, reason } = res;
  let tone = null;
  let title = '';
  let desc = '';
  if (enabled === false) {
    tone = 'neutral';
    title = '외주 관리자 연동 준비 중';
    desc = '이 화면은 관리자 설정이 켜진 뒤 표시됩니다. (MODERNWASH_VENDOR_PARITY_ENABLED)';
  } else if (reason) {
    tone = 'neutral';
    title = '연동 매장 안내';
    desc = reason;
  } else if (source?.error) {
    tone = 'error';
    title = '외주 원천 조회 실패';
    desc = source.error.message || '외주 관리자에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.';
  } else if (source?.degraded) {
    tone = 'warn';
    title = '응답 지연';
    desc = '외주 원천 응답이 느립니다. 데이터는 유효하지만 최신 반영이 지연될 수 있습니다.';
  } else {
    return null;
  }
  const palette = {
    neutral: { bg: '#F8FAFC', border: '#E2E8F0', color: '#334155' },
    warn: { bg: '#FFFBEB', border: '#FDE68A', color: '#92400E' },
    error: { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B' },
  }[tone];
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, md: 1.75 },
        borderRadius: '12px',
        backgroundColor: palette.bg,
        borderColor: palette.border,
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: palette.color }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: palette.color, opacity: 0.9, mt: 0.25 }}>{desc}</Typography>
    </Paper>
  );
}

/** 본문(표)을 그릴지 여부 — 비활성/비연동/원천오류면 배너만 노출. */
export function shouldRenderBody(res) {
  if (!res) return false;
  if (res.enabled === false) return false;
  if (res.reason) return false;
  if (res.source?.error) return false;
  return true;
}

/** master 매장 목록 조회 훅. */
export function useMasterStores(isMaster) {
  const [stores, setStores] = useState([]);
  useEffect(() => {
    if (!isMaster) return undefined;
    let alive = true;
    api
      .get('/stores')
      .then((r) => alive && setStores(r.data || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isMaster]);
  return stores;
}

const fieldSx = {
  height: 34,
  borderRadius: '8px',
  border: '1px solid rgba(0,0,0,0.16)',
  px: 1,
  fontSize: 13,
  fontFamily: 'inherit',
  backgroundColor: '#fff',
};

export const DateField = ({ label, value, max, min, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
    <Box
      component="input"
      type="date"
      aria-label={label}
      value={value}
      max={max}
      min={min}
      onChange={(e) => onChange(e.target.value)}
      sx={fieldSx}
    />
  </Box>
);

export const TextField = ({ label, value, placeholder, onChange, onEnter }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
    <Box
      component="input"
      type="text"
      aria-label={label}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && onEnter && onEnter()}
      sx={{ ...fieldSx, minWidth: 140 }}
    />
  </Box>
);

export const StoreSelect = ({ stores, value, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>매장</Typography>
    <Box
      component="select"
      aria-label="매장"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={fieldSx}
    >
      <option value="">전체(연동 매장)</option>
      {stores.map((s) => (
        <option key={s._id} value={s._id}>
          {s.name}
        </option>
      ))}
    </Box>
  </Box>
);

/**
 * 클라이언트 컬럼 정렬 훅(벤더의 컬럼 헤더 정렬 재현).
 * key 클릭 시 asc↔desc 토글, 다른 key 면 asc 로 시작.
 */
export function useColumnSort(items) {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const toggle = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const sorted = useMemo(() => {
    if (!sort.key) return items;
    const arr = [...items];
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), 'ko');
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [items, sort]);
  return { sorted, sort, toggle };
}
